import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { LOG_TYPES } from '../../constants/logTypeRegistry';
import {
  DATE_REVIEW_LABEL,
  REVIEW_LABEL,
  buildRawCaptureTimelinePreviewItems,
  groupRawCaptureTimelinePreviewItemsByDate,
} from '../../utils/rawCaptureTimelinePreviewItems';

const getCategoryConfig = (categoryKey) => LOG_TYPES[categoryKey] || LOG_TYPES.log;

const PreviewChip = ({ label, palette, subtle = false }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      bgcolor: subtle ? '#fff' : palette.softBg,
      border: `1px solid ${palette.border}`,
      color: palette.text,
      fontWeight: 700,
      maxWidth: '100%',
      '& .MuiChip-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }}
  />
);

const TimelinePreviewCard = ({ item }) => {
  const config = getCategoryConfig(item.categoryKey);
  const palette = config.palette || LOG_TYPES.log.palette;
  const Icon = config.iconComponent;
  const visibleBuckets = item.bucketGroups?.length ? item.bucketGroups : item.factGroups;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1,
        borderColor: palette.border,
        bgcolor: '#fff',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1,
              bgcolor: palette.softBg,
              color: palette.text,
              border: `1px solid ${palette.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontWeight: 800,
            }}
          >
            {Icon ? <Icon fontSize="small" /> : config.displayLabel?.slice(0, 1)}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 0.75 }}>
              <PreviewChip label={config.displayLabel || 'Daily Log'} palette={palette} />
              <PreviewChip label={item.sourceLabel} palette={palette} subtle />
              {item.confidence ? <PreviewChip label={`Confidence: ${item.confidence}`} palette={palette} subtle /> : null}
              {item.needsReview ? <Chip label={REVIEW_LABEL} size="small" color="warning" /> : null}
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#253248', lineHeight: 1.25 }}>
              {item.title}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          <Chip label={item.timeLabel} size="small" variant="outlined" />
          {item.dateLabel !== DATE_REVIEW_LABEL ? <Chip label={item.dateLabel} size="small" variant="outlined" /> : null}
        </Stack>

        {item.body ? (
          <Typography variant="body2" sx={{ color: '#526071', whiteSpace: 'pre-wrap' }}>
            {item.body}
          </Typography>
        ) : null}

        {item.details.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            {item.details.slice(0, 6).map((detail) => (
              <Box key={`${detail.label}-${detail.value}`} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {detail.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#253248' }}>
                  {detail.value}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        {visibleBuckets.length ? (
          <Stack spacing={1}>
            {visibleBuckets.slice(0, 8).map((group) => (
              <Box
                key={`${group.key}-${group.label}`}
                sx={{
                  borderLeft: `3px solid ${palette.border}`,
                  pl: 1.25,
                  py: 0.25,
                }}
              >
                <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: palette.text, fontWeight: 800 }}>
                    {group.label || group.key}
                  </Typography>
                  {group.role ? (
                    <Chip label={group.role} size="small" variant="outlined" sx={{ height: 20 }} />
                  ) : null}
                  {group.needsReview ? (
                    <Chip label={REVIEW_LABEL} size="small" color="warning" sx={{ height: 20 }} />
                  ) : null}
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {group.values.slice(0, 4).map((value) => (
                    <Chip key={`${group.key}-${value}`} label={value} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

const RawCaptureTimelinePreview = ({
  title = 'Timeline preview',
  description,
  records,
  candidates,
  emptyLabel = 'No timeline cards to preview.',
}) => {
  const items = buildRawCaptureTimelinePreviewItems({ records, candidates });
  const groupedItems = groupRawCaptureTimelinePreviewItemsByDate(items);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title} ({items.length})
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
      </Box>

      {items.length ? (
        <Stack spacing={2}>
          {Object.entries(groupedItems).map(([dateLabel, dateItems]) => (
            <Box key={dateLabel}>
              <Typography
                variant="overline"
                sx={{ color: '#667085', fontWeight: 800, letterSpacing: 0 }}
              >
                {dateLabel}
              </Typography>
              <Stack spacing={1.25}>
                {dateItems.map((item) => (
                  <TimelinePreviewCard key={item.id} item={item} />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Alert severity="info">{emptyLabel}</Alert>
      )}
    </Stack>
  );
};

export default RawCaptureTimelinePreview;
